import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Table from "@/components/ui/table";
import type { TableColumn } from "@/types/component";
import ReportDetailLayout from "../../components/ReportDetailLayout";
import ReportFilterBar, {
  type ReportFilterState,
} from "../../components/ReportFilterBar";
import ReportKPICard from "../../components/ReportKPICard";
import ReportChart from "../../components/ReportChart";
import ReportExportButtons from "../../components/ReportExportButtons";
import { useReportData } from "../../hooks/useReportData";
import { useReportCategories } from "../../hooks/useReportCategories";
import { useReportConfig } from "../../hooks/useReportConfig";
import {
  reportsExtendedApi,
  type DepartmentInvestmentResponse,
  type DepartmentInvestmentItem,
  type DepartmentInvestmentParams,
} from "../../services/reports-extended.api";
import { Department, Finance, Recent, Approve } from "@/components/icons";

const PAGE_SIZE = 20;

const formatCurrency = (v: number) =>
  new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency: "TRY",
    maximumFractionDigits: 0,
  }).format(v);

const DepartmentInvestmentReportPage: React.FC = () => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<ReportFilterState>({});
  const [page, setPage] = useState(1);

  const categories = useReportCategories();

  const params: DepartmentInvestmentParams = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      dateFrom: filter.dateFrom,
      dateTo: filter.dateTo,
      departmentID: filter.departmentID,
      search: filter.search?.trim() || undefined,
      categoryIds: filter.categoryIds,
    }),
    [filter, page],
  );

  const { data, isLoading, error } = useReportData<
    DepartmentInvestmentResponse,
    DepartmentInvestmentParams
  >({
    fetcher: (p, signal) =>
      reportsExtendedApi.getDepartmentInvestment(p, signal),
    params,
  });

  const summary = data?.summary;
  const items = data?.items ?? [];
  const count = data?.count ?? 0;

  const topByCost = [...items]
    .sort((a, b) => b.totalCost - a.totalCost)
    .slice(0, 10);

  const columns: TableColumn<DepartmentInvestmentItem>[] = [
    { header: t("departmentInvestment.headerDepartment"), accessor: (row) => row.name },
    {
      key: "userCount",
      label: t("departmentInvestment.headerUser"),
      header: t("departmentInvestment.headerUser"),
      accessor: (row) => row.userCount,
    },
    {
      key: "enrollmentCount",
      label: t("departmentInvestment.headerEnrollment"),
      header: t("departmentInvestment.headerEnrollment"),
      accessor: (row) => row.enrollmentCount,
    },
    {
      key: "completedCount",
      label: t("departmentInvestment.headerCompleted"),
      header: t("departmentInvestment.headerCompleted"),
      accessor: (row) => row.completedCount,
    },
    {
      key: "totalEducationHours",
      label: t("departmentInvestment.headerEducationHours"),
      header: t("departmentInvestment.headerEducationHours"),
      accessor: (row) =>
        t("departmentInvestment.kpiTotalHoursValue", {
          hours: row.totalEducationHours,
        }),
    },
    {
      key: "totalCost",
      label: t("departmentInvestment.headerTotalCost"),
      header: t("departmentInvestment.headerTotalCost"),
      accessor: (row) => formatCurrency(row.totalCost),
    },
    {
      key: "costPerUser",
      label: t("departmentInvestment.headerPerPerson"),
      header: t("departmentInvestment.headerPerPerson"),
      accessor: (row) => formatCurrency(row.costPerUser),
    },
    {
      key: "completionRate",
      label: t("departmentInvestment.headerCompletion"),
      header: t("departmentInvestment.headerCompletion"),
      accessor: (row) => `%${row.completionRate.toFixed(1)}`,
    },
  ];

  // R11 yapılandırılabilirliği: kolon ayarları, kayıtlı filtre setleri,
  // zamanlanmış e-posta raporu. Blok düzenini ReportDetailLayout yönetir.
  const cfg = useReportConfig<DepartmentInvestmentItem>({
    reportKey: "DEPARTMENT_INVESTMENT",
    reportTitle: t("departmentInvestment.title"),
    columns,
  });

  useEffect(() => {
    if (cfg.defaultFilters) setFilter(cfg.defaultFilters as ReportFilterState);
  }, [cfg.defaultFilters]);

  return (
    <ReportDetailLayout
      title={t("departmentInvestment.title")}
      layoutKey={`report:DEPARTMENT_INVESTMENT`}
      presetSlot={cfg.presetBar(filter as Record<string, unknown>, (f) =>
        setFilter(f as ReportFilterState),
      )}
      modalSlot={cfg.scheduleModal(filter as Record<string, unknown>)}
      categoryLabel={t("reportCatalog.categories.financial.title")}
      description={t("departmentInvestment.description")}
      exportSlot={
        <>
          {cfg.toolbar}
          <ReportExportButtons
          reportType="DEPARTMENT_INVESTMENT"
          filters={params as unknown as Record<string, unknown>}
          filenameBase="birim-yatirim"
          exportFn={reportsExtendedApi.exportReport}
          disabled={isLoading}
        />
        </>
      }
      filterSlot={
        <ReportFilterBar
          categories={categories}
          showCategory
          value={filter}
          onChange={(next) => {
            setFilter(next);
            setPage(1);
          }}
        />
      }
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 14,
        }}
      >
        <ReportKPICard
          title={t("departmentInvestment.kpiTotalCost")}
          value={summary ? formatCurrency(summary.totalCost) : "—"}
          icon={<Finance width={18} height={18} />}
        />
        <ReportKPICard
          title={t("departmentInvestment.kpiTotalHours")}
          value={
            summary
              ? t("departmentInvestment.kpiTotalHoursValue", {
                  hours: summary.totalHours.toFixed(0),
                })
              : "—"
          }
          icon={<Recent width={18} height={18} />}
        />
        <ReportKPICard
          title={t("departmentInvestment.avgPerPerson")}
          value={summary ? formatCurrency(summary.avgCostPerUser) : "—"}
          icon={<Department width={18} height={18} />}
          accent="success"
        />
        <ReportKPICard
          title={t("departmentInvestment.kpiAvgCompletion")}
          value={summary ? `%${summary.avgCompletionRate.toFixed(1)}` : "—"}
          icon={<Approve width={18} height={18} />}
        />
      </div>

      <ReportChart
        title={t("departmentInvestment.topByCostTitle")}
        type="bar"
        isLoading={isLoading}
        isEmpty={topByCost.length === 0}
        series={[
          {
            name: t("departmentInvestment.totalCostSeries"),
            data: topByCost.map((i) => i.totalCost),
          },
        ]}
        options={{
          xaxis: { categories: topByCost.map((i) => i.name) },
          plotOptions: {
            bar: { horizontal: true, borderRadius: 4, barHeight: "70%" },
          },
          colors: ["#4f46e5"],
          yaxis: {
            labels: {
              formatter: (val: number) => formatCurrency(val),
            },
          },
        }}
      />

      {cfg.columnSettings}

      <Table
        columns={cfg.columns}
        data={items}
        isLoading={isLoading}
        getRowId={(row) => row.departmentID}
        totalRecords={count}
        currentPage={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        emptyMessage={error ? t("departmentInvestment.noData") : t("departmentInvestment.noDepartment")}
      />
    </ReportDetailLayout>
  );
};

export default DepartmentInvestmentReportPage;
